import { RenderableAsset } from "lib/types/assets/RenderableAsset";
import { AssetType } from "lib/types/assets/AssetType";
import { AttachablePluginInterface } from "./CesiumWrapper";
import { Guard } from "lib/actions/util";
import { Viewer } from "cesium";
import {
  ObservableProperty,
  ReadonlyObservableProperty,
} from "./ObservableProperty";
import {
  TilesetAssetTypeAdapter,
  AssetTypeAdapterInterface,
  ScansetAssetTypeAdapter,
  GeoJsonAssetTypeAdapter,
  RasterLayerAssetTypeAdapter,
} from "./AssetTypeAdapters";
import CameraPlugin from "./CameraPlugin";
import TilesetPlugin from "./TilesetPlugin";
import GeoJsonPlugin from "./GeoJsonPlugin";
import RasterLayerPlugin from "./RasterLayerPlugin";
import ScansetPlugin from "./ScansetPlugin";
import TilesetStylingPlugin from "./TilesetStylingPlugin";

export type AssetAdapterKind =
  | "tileset"
  | "geoJson"
  | "rasterLayer"
  | "scanset";

export type AssetState = {
  contentUri: string;
  type: AssetType;
  /** The adapter the asset was first shown with, which every later call uses. */
  adapterKind: AssetAdapterKind;
  assetId: number;
  /** True when the asset was shown directly (not via a layer). */
  isShowing: boolean;
  isReady: boolean;
  /** Ids of the layers currently keeping this asset rendered. */
  layerIds: number[];
};

function addLayerId(layerIds: number[], layerId: number): number[] {
  return layerIds.includes(layerId) ? layerIds : [...layerIds, layerId];
}

/**
 * Whether the asset is currently rendered, either because it was shown directly
 * or because a layer is keeping it visible. Distinct from `isShowing` alone,
 * which is what the asset show/hide toggles bind to.
 */
export function isAssetVisible(state: AssetState | null | undefined): boolean {
  return !!state && (state.isShowing || state.layerIds.length > 0);
}

export default class AssetsPlugin implements AttachablePluginInterface {
  private readonly _tilesetAdapter: TilesetAssetTypeAdapter;
  private readonly _geoJsonAdapter: GeoJsonAssetTypeAdapter;
  private readonly _rasterLayerAdapter: RasterLayerAssetTypeAdapter;
  private readonly _scansetAdapter: ScansetAssetTypeAdapter;

  private readonly _assets = new ObservableProperty(
    new Map<string, AssetState>(),
  );
  private readonly _layers = new ObservableProperty(new Set<number>());
  private _flyToContentUri: string = null;

  get assets(): ReadonlyObservableProperty<Map<string, AssetState>> {
    return this._assets;
  }

  get layers(): ReadonlyObservableProperty<Set<number>> {
    return this._layers;
  }

  showLayer(layerId: number): void {
    const updated = new Set(this._layers.getSnapshot());
    updated.add(layerId);
    this._layers.set(updated);
  }

  hideLayer(layerId: number): void {
    const updated = new Set(this._layers.getSnapshot());
    updated.delete(layerId);
    this._layers.set(updated);
  }

  constructor(
    private readonly _tilesetPlugin: TilesetPlugin,
    private readonly _geoJsonPlugin: GeoJsonPlugin,
    private readonly _rasterLayerPlugin: RasterLayerPlugin,
    private readonly _scansetPlugin: ScansetPlugin,
    private readonly _camera: CameraPlugin,
    private readonly _tilesetStylingPlugin: TilesetStylingPlugin,
  ) {
    this._tilesetAdapter = new TilesetAssetTypeAdapter(
      this._tilesetPlugin,
      this._camera,
      this._tilesetStylingPlugin,
    );
    this._geoJsonAdapter = new GeoJsonAssetTypeAdapter(
      this._geoJsonPlugin,
      this._camera,
    );
    this._rasterLayerAdapter = new RasterLayerAssetTypeAdapter(
      this._rasterLayerPlugin,
      this._camera,
    );
    this._scansetAdapter = new ScansetAssetTypeAdapter(
      this._scansetPlugin,
      this._camera,
    );
  }

  setViewer(_: Viewer): void {
    //we don't use the viewer, so don't keep track of it
  }

  detachViewer(): void {
    this._assets.set(new Map());
    this._layers.set(new Set());
    this._flyToContentUri = null;
  }

  async show(version: RenderableAsset) {
    Guard.require(version, "version");
    const uri = version.contentUri;
    const state = this.getState(uri) ?? {
      contentUri: uri,
      type: version.asset.type,
      adapterKind: this.getAdapterKind(version),
      assetId: version.asset.id,
      isShowing: false,
      isReady: false,
      layerIds: [],
    };
    this.setState(uri, { ...state, isShowing: true });
    const adapter = this.getAdapter(state.adapterKind);
    if (!adapter.isExisting(uri)) {
      try {
        await adapter.load(version);
      } catch (e) {
        this.setState(uri, { ...this.getState(uri), isShowing: false });
        // Whichever caller started the load, a pending fly to this asset can no
        // longer happen, so drop it rather than flying on a later show.
        if (this._flyToContentUri === uri) this._flyToContentUri = null;
        throw e;
      }
      this.setState(uri, { ...this.getState(uri), isReady: true });
      await this.onAssetReady(uri, adapter);
    }
    if (!adapter.isShowing(uri)) {
      adapter.show(uri);
    }
  }

  hide(contentUri: string) {
    const state = this.getState(contentUri);
    if (state) {
      this.setState(contentUri, { ...state, isShowing: false });
      if (state.layerIds.length === 0) {
        const adapter = this.getAdapter(state.adapterKind);
        if (adapter.isShowing(state.contentUri)) {
          adapter.hide(state.contentUri);
        }
      }
    }
  }

  hideAll() {
    const updated = new Map(this._assets.getSnapshot());
    for (const state of updated.values()) {
      const adapter = this.getAdapter(state.adapterKind);
      if (adapter.isShowing(state.contentUri)) {
        adapter.hide(state.contentUri);
      }
      updated.set(state.contentUri, {
        ...state,
        isShowing: false,
        layerIds: [],
      });
    }
    this._assets.set(updated);
    this._layers.set(new Set());
  }

  async showViaLayer(version: RenderableAsset, layerId: number): Promise<void> {
    Guard.require(version, "version");
    const uri = version.contentUri;

    const existing = this.getState(uri) ?? {
      contentUri: uri,
      type: version.asset.type,
      adapterKind: this.getAdapterKind(version),
      assetId: version.asset.id,
      isShowing: false,
      isReady: false,
      layerIds: [],
    };
    this.setState(uri, {
      ...existing,
      layerIds: addLayerId(existing.layerIds, layerId),
    });

    const adapter = this.getAdapter(existing.adapterKind);
    if (!adapter.isExisting(uri)) {
      try {
        await adapter.load(version);
      } catch (e) {
        // Detach the layer we just attached, so a failed load does not leave the
        // asset looking as though this layer is keeping it rendered.
        const state = this.getState(uri);
        this.setState(uri, {
          ...state,
          layerIds: state.layerIds.filter((id) => id !== layerId),
        });
        throw e;
      }
      this.setState(uri, { ...this.getState(uri), isReady: true });
    }
    if (!adapter.isShowing(uri)) {
      adapter.show(uri);
    }
  }

  hideViaLayer(contentUri: string, layerId: number): void {
    const state = this.getState(contentUri);
    if (!state || !state.layerIds.includes(layerId)) return;

    const layerIds = state.layerIds.filter((id) => id !== layerId);
    this.setState(contentUri, { ...state, layerIds });

    if (layerIds.length === 0 && !state.isShowing) {
      const adapter = this.getAdapter(state.adapterKind);
      if (adapter.isShowing(contentUri)) {
        adapter.hide(contentUri);
      }
    }
  }

  hideAllByAssetId(assetId: number) {
    const updated = new Map(this._assets.getSnapshot());
    for (const state of updated.values()) {
      if (state.assetId === assetId) {
        const adapter = this.getAdapter(state.adapterKind);
        if (adapter.isShowing(state.contentUri)) {
          adapter.hide(state.contentUri);
        }
        updated.set(state.contentUri, {
          ...state,
          isShowing: false,
          layerIds: [],
        });
      }
    }
    this._assets.set(updated);
  }

  async toggleShowing(version: RenderableAsset) {
    Guard.require(version, "version");
    const uri = version.contentUri;
    const state = this.getState(uri);
    if (state?.isShowing) {
      this.hide(uri);
    } else {
      await this.show(version);
    }
  }

  async flyTo(version: RenderableAsset) {
    Guard.require(version, "version");
    this._scansetPlugin.clearSelectedScan();
    const uri = version.contentUri;
    this._flyToContentUri = uri;
    const adapter = this.getAdapter(
      this.getState(uri)?.adapterKind ?? this.getAdapterKind(version),
    );
    if (adapter.isReady(uri) && adapter.isShowing(uri)) {
      // Already loaded and visible (e.g. shown via a layer) — just move the camera
      // without marking the asset as directly shown, which would block layer deselect
      await adapter.flyTo(uri);
    } else if (adapter.isReady(uri)) {
      await this.show(version);
      await adapter.flyTo(uri);
    } else {
      await this.show(version); //onAssetReady will handle the flyTo after ready
    }
  }

  cancelPendingFlyTo() {
    this._flyToContentUri = null;
  }

  private getState(contentUri: string) {
    return this._assets.getSnapshot().get(contentUri) ?? null;
  }

  private setState(contentUri: string, value: AssetState) {
    const updated = new Map(this._assets.getSnapshot());
    updated.set(contentUri, value);
    this._assets.set(updated);
    return value;
  }

  private getAdapterKind(version: RenderableAsset): AssetAdapterKind {
    const type = version.asset.type;
    switch (type) {
      case "model":
      case "pointCloud":
      case "layer":
        return "tileset";
      case "gisSimpleFeature":
        // A GIS asset with no vector content is legacy GeoJSON.
        return version.vectorContentUri ? "tileset" : "geoJson";
      case "rasterImagery":
        return "rasterLayer";
      case "scanset":
      case "scanSet": //for legacy
        return "scanset";
      default:
        throw new Error(`No adapter found for asset type '${type}'.`);
    }
  }

  private getAdapter(kind: AssetAdapterKind): AssetTypeAdapterInterface {
    switch (kind) {
      case "tileset":
        return this._tilesetAdapter;
      case "geoJson":
        return this._geoJsonAdapter;
      case "rasterLayer":
        return this._rasterLayerAdapter;
      case "scanset":
        return this._scansetAdapter;
    }
  }

  private async onAssetReady(
    contentUri: string,
    adapter: AssetTypeAdapterInterface,
  ) {
    if (this._flyToContentUri === contentUri) await adapter.flyTo(contentUri);
  }
}
